import {
  Box,
  Button,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Chip from '@mui/material/Chip';
import { IconShoppingCart } from '@tabler/icons-react';

interface AlaCarteCredits {
  case_credits: number;
  demand_letter_credits: number;
  deposition_credits: number;
}

interface AlaCartePlanCardProps {
  alaCarteCredits: AlaCarteCredits;
  onButtonClick: () => void;
  hasCaseSubscription?: boolean;
  /** Leave out the button that opens the plans dialog (a firm member). */
  hideButton?: boolean;
}

function AlaCartePlanCard({
  alaCarteCredits,
  onButtonClick,
  hasCaseSubscription = false,
  hideButton = false,
}: AlaCartePlanCardProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { case_credits, demand_letter_credits, deposition_credits } =
    alaCarteCredits;
  const totalCredits =
    case_credits + demand_letter_credits + deposition_credits;

  if (totalCredits === 0) {
    return null;
  }

  return (
    <Stack spacing={1} flexGrow={1} width="100%">
      <Stack
        direction={isMobile ? 'column' : 'row'}
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        sx={{
          p: 2,
          border: '1px solid',
          borderColor: theme.palette.success.main,
          borderRadius: 2,
          backgroundColor: 'background.paper',
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Stack spacing={1} direction="column">
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  backgroundColor: theme.palette.success.light,
                }}
              >
                <IconShoppingCart
                  size={20}
                  color={theme.palette.success.main}
                />
              </Box>

              <Typography variant="body1" fontWeight={500}>
                À la Carte Credits
              </Typography>

              <Chip
                label="Active"
                size="small"
                sx={{
                  color: theme.palette.success.contrastText,
                  backgroundColor: theme.palette.success.main,
                  fontWeight: 600,
                }}
              />
            </Stack>

            <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ pl: 0.5 }}>
              {case_credits > 0 && (
                <Chip
                  label={`${case_credits} Case Credit${case_credits !== 1 ? 's' : ''}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    color: theme.palette.text.primary,
                    borderColor: theme.palette.divider,
                  }}
                />
              )}
              {demand_letter_credits > 0 && (
                <Chip
                  label={`${demand_letter_credits} Demand Letter Credit${demand_letter_credits !== 1 ? 's' : ''}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    color: theme.palette.text.primary,
                    borderColor: theme.palette.divider,
                  }}
                />
              )}
              {deposition_credits > 0 && (
                <Chip
                  label={`${deposition_credits} Deposition Credit${deposition_credits !== 1 ? 's' : ''}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    color: theme.palette.text.primary,
                    borderColor: theme.palette.divider,
                  }}
                />
              )}
            </Stack>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ pl: 0.5 }}
            >
              Pay-as-you-go credits for individual features. No recurring
              subscription.
            </Typography>
          </Stack>
        </Box>

        {!hideButton && (
          <Stack direction="row" spacing={2}>
            <Button
              variant={hasCaseSubscription ? 'outlined' : 'contained'}
              onClick={onButtonClick}
            >
              {hasCaseSubscription ? 'Buy More Credits' : 'Upgrade Plan'}
            </Button>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}

export default AlaCartePlanCard;
